import { createSlice, type PayloadAction } from "@reduxjs/toolkit"
import type { IDisplayField, IVlpCalculator, unitId, VLPCriteria } from "../../interfaces/interfaces";
import { apiSlice } from "./apiSlice";
import { UnitConverter } from "../../components/MeasurementUnits/UnitConverter";


const initialState: IVlpCalculator = {
	inputs: [
		{
			id: 'vlp-wellhead-temperature',
			type: 'string',
			label: '',
			queryName: 'temperature_wh',
			baseUnitId: 'celsius',
			baseValue: 15.6,
			currentValue: 15.6,
			currentUnitId: 'celsius',
			unitCategoryId: 'temperature',
			userValue: null,
			userUnitId: null,
			min: -273.15,
			// max: 1.1
		},
		{
			id: 'vlp-reservoir-temperature',
			type: 'string',
			label: '',
			queryName: 'temperature_res',
			baseUnitId: 'celsius',
			baseValue: 80,
			currentValue: 80,
			currentUnitId: 'celsius',
			unitCategoryId: 'temperature',
			userValue: null,
			userUnitId: null,
			min: 0,
		},
		{
			id: 'vlp-max-wellrate', //Maximum wellrate for VLP calculation
			type: 'string',
			label: '',
			queryName: 'gas_rate_max',
			baseUnitId: 'meter',
			baseValue: 1000,
			currentValue: 1000,
			currentUnitId: null,
			unitCategoryId: null,
			userValue: null,
			userUnitId: null,
			min: 0,
		},
		{
			id: 'vlp-cgr',
			type: 'string',
			label: '',
			queryName: 'cgr',
			baseUnitId: null,
			baseValue: 0,
			currentValue: 0,
			currentUnitId: null,
			unitCategoryId: null,
			userValue: null,
			userUnitId: null,
			// min: 0,
		},
		{
			id: 'vlp-condensate-specific-gravity',
			type: 'string',
			label: '',
			queryName: 'y_c',
			baseUnitId: null,
			baseValue: 0.72,
			currentValue: 0.72,
			currentUnitId: null,
			unitCategoryId: null,
			userValue: null,
			userUnitId: null,
			// min: 0,
		},
		{
			id: 'vlp-wgr',
			type: 'string',
			label: '',
			queryName: 'wgr',
			baseUnitId: null,
			baseValue: 100,
			currentValue: 100,
			currentUnitId: null,
			unitCategoryId: null,
			userValue: null,
			userUnitId: null,
			// min: 0,
			//20 dyne/cm - для конденсата
			//60 dyne/cm - для воды
		},
		{
			id: 'vlp-water-specific-gravity',
			type: 'string',
			label: '',
			queryName: 'y_w',
			baseUnitId: null,
			baseValue: 1.015,
			currentValue: 1.015,
			currentUnitId: null,
			unitCategoryId: null,
			userValue: null,
			userUnitId: null,
			min: 0,
		},
		{
			id: 'vlp-tvd',
			type: 'string',
			label: '',
			queryName: 'tvd',
			baseUnitId: 'meter',
			baseValue: 2200,
			currentValue: 2200,
			currentUnitId: 'meter',
			unitCategoryId: 'length',
			userValue: null,
			userUnitId: null,
			min: 0,
		},
		{
			id: 'vlp-md',
			type: 'string',
			label: '',
			queryName: 'md',
			baseUnitId: 'meter',
			baseValue: 2200,
			currentValue: 2200,
			currentUnitId: 'meter',
			unitCategoryId: 'length',
			userValue: null,
			userUnitId: null,
			min: 0,
		},
		{
			id: 'vlp-tubing-inside-roughness',
			type: 'string',
			label: '',
			queryName: 'roughness',
			baseUnitId: 'meter',
			baseValue: 0.000015,
			currentValue: 0.000015,
			currentUnitId: 'meter',
			unitCategoryId: 'length',
			userValue: null,
			userUnitId: null,
			min: 0,
		},
		{
			id: 'vlp-gravity-multiplier',
			type: 'string',
			label: '',
			queryName: 'mult_gravity',
			baseUnitId: null,
			baseValue: 1,
			currentValue: 1,
			currentUnitId: null,
			unitCategoryId: null,
			userValue: null,
			userUnitId: null,
			min: 0,
		},
		{
			id: 'vlp-friction-multiplier',
			type: 'string',
			label: '',
			queryName: 'mult_friction',
			baseUnitId: null,
			baseValue: 1,
			currentValue: 1,
			currentUnitId: null,
			unitCategoryId: null,
			userValue: null,
			userUnitId: null,
			min: 0,
		},
	],
	well1: {
		inputs: [
			{
				id: 'well1-flowing-wellhead-pressure',
				type: 'string',
				label: '',
				queryName: 'pressure_wh',
				baseUnitId: 'bar',
				baseValue: 100,
				currentValue: 100,
				currentUnitId: 'bar',
				unitCategoryId: 'pressure',
				userValue: null,
				userUnitId: null,
				min: 0,
			},
			{
				id: 'well1-tubing-inside-diameter',
				type: 'string',
				label: '',
				queryName: 'tubing_id',
				baseUnitId: 'meter',
				baseValue: 0.062,
				currentValue: 0.062,
				currentUnitId: 'meter',
				unitCategoryId: 'length',
				userValue: null,
				userUnitId: null,
				min: 0,
			},
		],
		criteria: {
			id: 'well1-vlp-criteria',
			selected: 'average_tz',
			options: ['average_tz', 'cullender'],
		}
	},
	well2: {
		inputs: [
			{
				id: 'well2-flowing-wellhead-pressure',
				type: 'string',
				label: '',
				queryName: 'pressure_wh',
				baseUnitId: 'bar',
				baseValue: 100,
				currentValue: 100,
				currentUnitId: 'bar',
				unitCategoryId: 'pressure',
				userValue: null,
				userUnitId: null,
				min: 0,
			},
			{
				id: 'well2-tubing-inside-diameter',
				type: 'string',
				label: '',
				queryName: 'tubing_id',
				baseUnitId: 'meter',
				baseValue: 0.072,
				currentValue: 0.072,
				currentUnitId: 'meter',
				unitCategoryId: 'length',
				userValue: null,
				userUnitId: null,
				min: 0,
			},
		],
		criteria: {
			id: 'well2-vlp-criteria',
			selected: 'average_tz',
			options: ['average_tz', 'cullender'],
		}
	},
	// result: {
	// 	id: "hydrate-result-temperature",
	// 	value: null,
	// 	type: "string",
	// 	label: "",
	// 	queryName: "temperature"
	// },
	downloadLink: '',
	chartConfig: {},
	requiredSubscriptionLevel: 'free'
}
const converter = new UnitConverter();
const vlpCalculatorSlice = createSlice({
	name: "vlpCalculator",
	initialState,
	selectors: {
		getDataForGRC: (state, wellId: "well1" | "well2"): { inputs: IDisplayField[], errors: string[] } => {
			validateInputs();
			const displayedFields: IDisplayField[] = [];
			const wellInputs = wellId === "well1" ? state.well1.inputs : state.well2.inputs;

			//well
			// 'pressure_wh',
			// 'tubing_id', 

			// 'pressure_res', 
			// 'temperature_res', 

			const requiredInputs = ["pressure_wh", "tubing_id", "temperature_res"];
			[...state.inputs, ...wellInputs].filter(input => requiredInputs.includes(input.queryName)).forEach(input => {
				displayedFields.push(
					{
						...input,
						displayValue: input.currentValue,
						translationKey: `inputs.${input.id}`,
						error: input?.currentValue != null ? input.error : "empty",
					}
				);
			});

			const errors: string[] = [];

			if (displayedFields.some(input => requiredInputs.includes(input.queryName) && !input.currentValue)) {
				errors.push("Empty");
			}

			return { inputs: displayedFields, errors: errors };
		},
		getDataForSystemPlot: (state, wellId: "well1" | "well2"): { inputs: IDisplayField[], errors: string[] } => {
			const displayedFields: IDisplayField[] = [];
			const vlpInputs = [...state.inputs, ...(wellId === "well1" ? state.well1.inputs : state.well2.inputs)];
			const criteria = wellId === "well1" ? state.well1.criteria.selected : state.well2.criteria.selected;
			const requiredInputs = ["vlp_criteria", "temperature_wh", "temperature_res", "gas_rate_max", "cgr", "y_c", "wgr", "y_w", "tvd", "md", "roughness", "mult_gravity", "mult_friction", "pressure_wh", "tubing_id"];
			validateInputs();
			requiredInputs.map(inputName => {
				if (inputName == "vlp_criteria") {
					displayedFields.push(
						{
							displayValue: criteria,
							translationKey: `vlp:labels.criteria`,
							baseValue: criteria,
							currentValue: criteria,
							type: "string",
							id: "vlp_criteria",
							unitCategoryId: null,
							baseUnitId: null,
							currentUnitId: null,
							label: "",
							queryName: "vlp_criteria",
							userValue: null,
							userUnitId: null,
						}
					);
					return;
				}

				const i = vlpInputs.find(input => input.queryName === inputName);
				displayedFields.push(
					{
						displayValue: i?.currentValue,
						translationKey: i?.queryName ? `vlp:inputs.${i?.id}` : inputName,
						baseValue: i?.baseValue ?? "",
						currentValue: i?.currentValue ?? null,
						type: "string",
						id: i?.id ?? "",
						unitCategoryId: i?.unitCategoryId ?? null,
						baseUnitId: i?.baseUnitId ?? null,
						currentUnitId: i?.currentUnitId ?? null,
						label: i?.label ?? "",
						queryName: i?.queryName ?? "",
						userValue: i?.userValue ?? null,
						userUnitId: i?.userUnitId ?? null,
						error: i?.currentValue != null ? i.error : "empty"
					}
				);
			});

			const errors: string[] = [];

			if (displayedFields.some(input => requiredInputs.includes(input.queryName) && input.currentValue == null)) {
				errors.push("missing_required_inputs");
			}

			return { inputs: displayedFields, errors: errors };
		},
	},
	reducers: {
		setInputValue: (state, action: PayloadAction<{ inputName: string, value: number | null, wellId?: "well1" | "well2" }>) => {
			if (!action.payload.wellId) {
				state.inputs.forEach(i => {
					if (i.queryName === action.payload.inputName) {
						i.currentValue = action.payload.value;
						i.baseValue = (i.unitCategoryId && action.payload.value != null) ? converter.convert(action.payload.value, i.currentUnitId, i.baseUnitId) : action.payload.value;
						i.userValue = action.payload.value;
						i.userUnitId = i.currentUnitId;
					}
				});
			} else {
				const wellInputs = state[action.payload.wellId]?.inputs;
				if (wellInputs) {
					wellInputs.forEach(i => {
						if (i.queryName === action.payload.inputName) {
							i.currentValue = action.payload.value;
							i.baseValue = (i.unitCategoryId && action.payload.value != null) ? converter.convert(action.payload.value, i.currentUnitId, i.baseUnitId) : action.payload.value;
							i.userValue = action.payload.value;
							i.userUnitId = i.currentUnitId;
						}
					});
				}
			}

			state.well1.results = undefined;
			state.well2.results = undefined;
			state.well1.chartsData = null;
			state.well2.chartsData = null;
		},
		setInputUnit: (state, action: PayloadAction<{ inputId: string, toUnitId: unitId | null, wellId?: "well1" | "well2" }>) => {
			if (action.payload.wellId) {
				const wellInputs = state[action.payload.wellId]?.inputs;
				if (!wellInputs)
					return;

				wellInputs.forEach(i => {
					if (i.id === action.payload.inputId) {
						i.currentValue = i.currentValue != null ? converter.convert(i.currentValue, i.currentUnitId, action.payload.toUnitId) : null;
						i.currentUnitId = action.payload.toUnitId;
						i.baseValue = i.userValue && i.userUnitId
							? converter.convert(i.userValue, i.userUnitId, i.baseUnitId)
							: converter.convert(i.currentValue ?? 0, i.currentUnitId, i.baseUnitId);
					}
				})
				return;
			}

			state.inputs.forEach(i => {
				if (i.id === action.payload.inputId) {
					i.currentValue = i.currentValue != null ? converter.convert(i.currentValue, i.currentUnitId, action.payload.toUnitId) : null;
					i.currentUnitId = action.payload.toUnitId;
					i.baseValue = i.userValue && i.userUnitId
						? converter.convert(i.userValue, i.userUnitId, i.baseUnitId)
						: converter.convert(i.currentValue ?? 0, i.currentUnitId, i.baseUnitId);

					if (i.errorParams?.["min"] && i.min) {
						i.errorParams = { min: converter.convert(i.min, i.baseUnitId, i.currentUnitId) };
					}
					if (i.errorParams?.["max"] && i.max) {
						i.errorParams = { max: converter.convert(i.max, i.baseUnitId, i.currentUnitId) };
					}
				}
			})
		},
		setCriteria: (state, action: PayloadAction<{ wellId: "well1" | "well2", criteria: VLPCriteria }>) => {
			state[action.payload.wellId].criteria.selected = action.payload.criteria;

			state[action.payload.wellId].results = undefined;
			state[action.payload.wellId].chartsData = null;
		},
		validateInputs: (state) => {
			[
				...state.inputs,
				...state.well1.inputs,
				...state.well2.inputs,
			].forEach(input => {
				if (input.min != null && (input.baseValue == null || input.baseValue < input.min)) {
					input.error = "validation.min_value_error";
					input.errorParams = { min: converter.convert(input.min, input.baseUnitId, input.currentUnitId) };
				} else if (input.max != null && input.baseValue != null && input.baseValue > input.max) {
					input.error = "validation.max_value_error";
					input.errorParams = { max: converter.convert(input.max, input.baseUnitId, input.currentUnitId) };
				} else {
					input.error = null;
					input.errorParams = null;
				}
			});
		},
		setFieldValue: (state, action: PayloadAction<{ inputId: string; value: number | null }>) => {
			const input = state.inputs.find(i => i.id === action.payload.inputId);
			if (input) {
				input.currentValue = action.payload.value;
				input.baseValue = (input.unitCategoryId && action.payload.value != null) ? converter.convert(action.payload.value, input.currentUnitId, input.baseUnitId) : action.payload.value;
				input.userValue = action.payload.value;
				input.userUnitId = input.currentUnitId;
			}
		},
		updateInputByUnit: (state, action: PayloadAction<{ inputId: string, selectedUnitId: unitId | null, wellId?: "well1" | "well2", }>) => {
			state.inputs.forEach(i => {
				if (i.id === action.payload.inputId) {
					i.currentValue = i.currentValue != null ? converter.convert(i.currentValue, i.currentUnitId, action.payload.selectedUnitId) : null;
					i.currentUnitId = action.payload.selectedUnitId;
					i.baseValue = i.userValue && i.userUnitId
						? converter.convert(i.userValue, i.userUnitId, i.baseUnitId)
						: converter.convert(i.currentValue ?? 0, i.currentUnitId, i.baseUnitId);
					return;
				}
			})
		},
		updateWellInputByUnit: (state, action: PayloadAction<{ wellId: "well1" | "well2", inputId: string, selectedUnitId: unitId | null }>) => {
			const wellInputs = state[action.payload.wellId]?.inputs;
			if (!wellInputs)
				return;

			wellInputs.forEach(i => {
				if (i.id === action.payload.inputId) {
					i.currentValue = i.currentValue != null ? converter.convert(i.currentValue, i.currentUnitId, action.payload.selectedUnitId) : null;
					i.currentUnitId = action.payload.selectedUnitId;
					i.baseValue = i.userValue && i.userUnitId
						? converter.convert(i.userValue, i.userUnitId, i.baseUnitId)
						: converter.convert(i.currentValue ?? 0, i.currentUnitId, i.baseUnitId);
					return;
				}
			})
		},

	},
	extraReducers: (builder) => {
		builder
			.addMatcher(apiSlice.endpoints.calculator.matchFulfilled, (state, action) => {
				const params = action.meta.arg.originalArgs as Record<string, string>;
				if (!params?.calculator_type) {
					console.warn("No calculator_type in calculator response");
					return;
				}
				if (params.calculator_type != "vlp") {
					return;
				}
				if (action.payload) {
					const well = params.well === "1" ? state.well1 : params.well === "2" ? state.well2 : null;
					if (well) {
						well.results = action.payload.results;
						well.chartsData = action.payload.plots;
					}
				}
			})
	}
})

export default vlpCalculatorSlice

export const {
	setCriteria,
	setInputValue,
	setInputUnit,
	validateInputs,
	setFieldValue,
	updateInputByUnit,
	updateWellInputByUnit,
} = vlpCalculatorSlice.actions

export const {
	getDataForGRC,
	getDataForSystemPlot
} = vlpCalculatorSlice.selectors;